/*
    The shape of every picture, for the lighting (0.98.7, the owner, 1 November 2026: "Lighting that shows each
    object's shape"): the parts without Pixi (tests/src/update0987.test.ts). A picture's normal map is baked from the
    picture itself (managers/normalLook.ts): its shape from its outline, rounded in from the edge like a pillow (a
    crate, a wall) or domed like a ball (a player, a tree's crown), and its painted detail (the boards of a crate, the
    grain of a floor) as fine bumps; so the sun and the lights can tell its lit side from its shaded side, and a flash
    beside a crate lights the near side of it and not the far side.

    A normal is stored as a colour: red the x slope (0.5 flat), green the y slope (down the screen, 0.5 flat), blue
    the up (1 flat), alpha the picture's own cover.
*/

/** how a picture's height is read off its outline */
export type NormalShape = "flat" | "bevel" | "dome";

export interface NormalProfile {
    readonly shape: NormalShape
    /** the bevel's width in from the edge, as a share of the picture's shorter side (bevel) */
    readonly bevel: number
    /** how steep the shape's slopes are drawn (1 modest, 3 bold) */
    readonly strength: number
    /** how much of the painted detail becomes bumps (0 none) */
    readonly detail: number
}

export const PROFILES: Readonly<Record<string, NormalProfile>> = Object.freeze({
    /** a thing standing: a crate, a barrel, a wall, a car, a stone: rounded in from its edge, its boards and rivets bumps */
    thing: { shape: "bevel", bevel: 0.22, strength: 2.2, detail: 0.5 },
    /** a wall: a narrow ridge, its top rounded */
    wall: { shape: "bevel", bevel: 0.45, strength: 2.6, detail: 0.25 },
    /** a player's body, an animal, a tree's crown, a bush: a dome */
    round: { shape: "dome", bevel: 1, strength: 2.4, detail: 0.3 },
    /** loot, gear, a weapon in hand: small and rounded */
    small: { shape: "bevel", bevel: 0.35, strength: 1.8, detail: 0.35 },
    /** a roof: flat, its edges rounded down (the eaves), its tiles and seams faint bumps */
    roof: { shape: "bevel", bevel: 0.06, strength: 1.6, detail: 0.35 },
    /** a floor, a rug, a decal, a thing lying flat: no shape, only the grain of what's painted on it */
    flat: { shape: "flat", bevel: 0, strength: 1, detail: 0.45 }
});

/** the normals' picture (managers/normalLook.ts) is this share of the screen's pixels each way (the shapes are soft) */
export const NORMAL_SCALE = 0.5;

/** the longest side a picture is baked at (pixels): the shapes are soft, drawn small and stretched */
export const BAKE_SIZE = 256;
/** a thin picture (a wall) is never baked thinner than this many pixels across */
export const BAKE_MIN = 10;

/** the scale a picture of `width` by `height` is baked at */
export function bakeScale(width: number, height: number): number {
    const long = Math.max(width, height), short = Math.max(1, Math.min(width, height));
    return Math.min(1, Math.max(BAKE_SIZE / long, BAKE_MIN / short));
}

/**
 * the squared Euclidean distance transform of a grid (Felzenszwalb and Huttenlocher): for each cell, the squared
 * distance to the nearest cell with `inside` false. `inside` as a byte per cell (0 outside)
 */
export function distanceSquared(inside: Uint8Array, width: number, height: number): Float32Array {
    const INF = 1e20;
    const f = new Float32Array(Math.max(width, height));
    const d = new Float32Array(Math.max(width, height));
    const v = new Int32Array(Math.max(width, height));
    const z = new Float32Array(Math.max(width, height) + 1);
    const out = new Float32Array(width * height);
    const transform1d = (n: number): void => {
        let k = 0;
        v[0] = 0;
        z[0] = -INF;
        z[1] = INF;
        for (let q = 1; q < n; q++) {
            let s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
            while (s <= z[k]) {
                k--;
                s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
            }
            k++;
            v[k] = q;
            z[k] = s;
            z[k + 1] = INF;
        }
        k = 0;
        for (let q = 0; q < n; q++) {
            while (z[k + 1] < q) k++;
            d[q] = (q - v[k]) * (q - v[k]) + f[v[k]];
        }
    };
    // columns first
    for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) f[y] = inside[y * width + x] ? INF : 0;
        transform1d(height);
        for (let y = 0; y < height; y++) out[y * width + x] = d[y];
    }
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) f[x] = out[y * width + x];
        transform1d(width);
        for (let x = 0; x < width; x++) out[y * width + x] = d[x];
    }
    return out;
}

/**
 * the squared distance of each cell inside the picture to the nearest cell outside it, the picture's frame counting as
 * outside (`distanceSquared` with a border of outside round the grid)
 */
export function distanceInside(inside: Uint8Array, width: number, height: number): Float32Array {
    const w = width + 2, h = height + 2;
    const padded = new Uint8Array(w * h);
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) padded[(y + 1) * w + x + 1] = inside[y * width + x];
    const d2 = distanceSquared(padded, w, h);
    const out = new Float32Array(width * height);
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) out[y * width + x] = d2[(y + 1) * w + x + 1];
    return out;
}

/** a box blur of a grid, `radius` cells each way, the edges clamped (two passes) */
export function boxBlur(values: Float32Array, width: number, height: number, radius: number): Float32Array {
    if (radius <= 0) return values;
    const pass = new Float32Array(width * height);
    const out = new Float32Array(width * height);
    const span = radius * 2 + 1;
    for (let y = 0; y < height; y++) {
        let sum = 0;
        for (let x = -radius; x <= radius; x++) sum += values[y * width + Math.min(width - 1, Math.max(0, x))];
        for (let x = 0; x < width; x++) {
            pass[y * width + x] = sum / span;
            const leave = Math.max(0, x - radius), enter = Math.min(width - 1, x + radius + 1);
            sum += values[y * width + enter] - values[y * width + leave];
        }
    }
    for (let x = 0; x < width; x++) {
        let sum = 0;
        for (let y = -radius; y <= radius; y++) sum += pass[Math.min(height - 1, Math.max(0, y)) * width + x];
        for (let y = 0; y < height; y++) {
            out[y * width + x] = sum / span;
            const leave = Math.max(0, y - radius), enter = Math.min(height - 1, y + radius + 1);
            sum += pass[enter * width + x] - pass[leave * width + x];
        }
    }
    return out;
}

/**
 * a picture's normal map from its pixels (straight RGBA, `width` by `height`), by its profile: RGBA bytes, the
 * normal as a colour, the alpha the picture's own cover. The picture's edge is where its alpha crosses a half; outside
 * it the normal is flat (and covered by nothing)
 */
export function bakeNormals(pixels: Uint8ClampedArray, width: number, height: number, profile: NormalProfile): Uint8ClampedArray {
    const count = width * height;
    const inside = new Uint8Array(count);
    const luma = new Float32Array(count);
    for (let i = 0; i < count; i++) {
        const a = pixels[i * 4 + 3];
        inside[i] = a >= 128 ? 1 : 0;
        luma[i] = (pixels[i * 4] * 0.299 + pixels[i * 4 + 1] * 0.587 + pixels[i * 4 + 2] * 0.114) / 255;
    }
    let shape: Float32Array = new Float32Array(count);
    const short = Math.max(1, Math.min(width, height));
    if (profile.shape !== "flat") {
        // (the picture's frame is its edge too: a crate that fills its frame falls away at the frame's border)
        const d2 = distanceInside(inside, width, height);
        let furthest = 0;
        for (let i = 0; i < count; i++) if (d2[i] > furthest) furthest = d2[i];
        const reach = Math.sqrt(furthest);
        if (profile.shape === "dome") {
            // a dome: its height the circle's over its distance in from the edge
            for (let i = 0; i < count; i++) {
                if (!inside[i]) continue;
                const t = Math.min(1, Math.sqrt(d2[i]) / Math.max(1, reach));
                shape[i] = Math.sqrt(1 - (1 - t) * (1 - t));
            }
        } else {
            // a bevel: up from the edge over its width, then flat (a pillow's)
            const bevel = Math.max(1.5, Math.min(reach, profile.bevel * short));
            for (let i = 0; i < count; i++) {
                if (!inside[i]) continue;
                const t = Math.min(1, Math.sqrt(d2[i]) / bevel);
                shape[i] = Math.sin(t * Math.PI / 2);
            }
        }
        // (softened: a jagged outline (a crown of leaves) makes ridges in the distance, and ridges make spikes of
        // shading; a blur of a few pixels rounds them off, more for a dome)
        shape = boxBlur(shape, width, height, Math.max(1, Math.round(short * (profile.shape === "dome" ? 0.05 : 0.015))));
    } else {
        for (let i = 0; i < count; i++) shape[i] = inside[i];
    }
    // the painted detail: the luma's fine part (the luma less its blur), inside the picture
    const detail = new Float32Array(count);
    if (profile.detail > 0) {
        const radius = 2;
        const pass = new Float32Array(count);
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                let sum = 0, n = 0;
                for (let k = -radius; k <= radius; k++) {
                    const xx = Math.min(width - 1, Math.max(0, x + k));
                    const j = y * width + xx;
                    if (!inside[j]) continue;
                    sum += luma[j];
                    n++;
                }
                pass[y * width + x] = n ? sum / n : 0;
            }
        }
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const i = y * width + x;
                if (!inside[i]) continue;
                let sum = 0, n = 0;
                for (let k = -radius; k <= radius; k++) {
                    const yy = Math.min(height - 1, Math.max(0, y + k));
                    const j = yy * width + x;
                    if (!inside[j]) continue;
                    sum += pass[j];
                    n++;
                }
                const blurred = n ? sum / n : luma[i];
                detail[i] = (luma[i] - blurred) * profile.detail;
            }
        }
    }
    const out = new Uint8ClampedArray(count * 4);
    const strengthShape = profile.strength * (profile.shape === "flat" ? 0 : profile.shape === "dome" ? Math.max(2, short * 0.18) : Math.max(1.5, profile.bevel * short * 0.8));
    const strengthDetail = 2.5;
    const at = (grid: Float32Array, x: number, y: number): number => grid[Math.min(height - 1, Math.max(0, y)) * width + Math.min(width - 1, Math.max(0, x))];
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const i = y * width + x;
            const a = pixels[i * 4 + 3];
            let nx = 0, ny = 0, nz = 1;
            if (inside[i]) {
                // (the slopes from the neighbours inside the picture: at the edge the shape falls to 0 outside)
                const sx = (at(shape, x + 1, y) - at(shape, x - 1, y)) * 0.5 * strengthShape + (at(detail, x + 1, y) - at(detail, x - 1, y)) * 0.5 * strengthDetail;
                const sy = (at(shape, x, y + 1) - at(shape, x, y - 1)) * 0.5 * strengthShape + (at(detail, x, y + 1) - at(detail, x, y - 1)) * 0.5 * strengthDetail;
                const len = Math.sqrt(sx * sx + sy * sy + 1);
                nx = -sx / len;
                ny = -sy / len;
                nz = 1 / len;
            }
            out[i * 4] = Math.round((nx * 0.5 + 0.5) * 255);
            out[i * 4 + 1] = Math.round((ny * 0.5 + 0.5) * 255);
            out[i * 4 + 2] = Math.round((nz * 0.5 + 0.5) * 255);
            out[i * 4 + 3] = a;
        }
    }
    return out;
}

/** a normal map's value at a pixel, back from its colour: x, y (down the screen) and z (up) */
export function readNormal(map: Uint8ClampedArray, index: number): { x: number, y: number, z: number } {
    return { x: map[index * 4] / 127.5 - 1, y: map[index * 4 + 1] / 127.5 - 1, z: map[index * 4 + 2] / 127.5 - 1 };
}

/** the turn a thing's normals take with it: its cosine and sine, and -1 for a mirrored picture (1 otherwise) */
export interface NormalTurn {
    readonly cos: number
    readonly sin: number
    readonly flip: number
}

/**
 * the turn a thing's normals take with it (its matrix a, b, c, d, as Pixi writes one: x' = a x + c y, y' = b x + d y):
 * the turn read off the picture's first axis, and `flip` -1 for a mirrored picture (a negative scale), mirrored then
 * along its second axis (the first's turn already carries a mirror along the first: 0.98.7's audit)
 */
export function normalTurn(a: number, b: number, c: number, d: number): NormalTurn {
    const angle = Math.atan2(b, a);
    return { cos: Math.cos(angle), sin: Math.sin(angle), flip: a * d - b * c < 0 ? -1 : 1 };
}

/** a map's normal (x, y) turned as the shader turns it (`NORMALS_FRAGMENT`): mirrored along its second axis, then turned */
export function turnNormal(x: number, y: number, turn: NormalTurn): [number, number] {
    const my = y * turn.flip;
    return [x * turn.cos - my * turn.sin, x * turn.sin + my * turn.cos];
}

/** the normals' picture's mesh (managers/normalLook.ts): each thing's quad, its turn for its normals. GLSL ES 1.0 as Pixi rewrites it */
export const NORMALS_VERTEX = /* glsl */`
in vec2 aPosition;
in vec2 aUV;
in vec3 aTurn;
in float aAlpha;
out vec2 vUV;
out vec3 vTurn;
out float vAlpha;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;

void main(void) {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
    vUV = aUV;
    vTurn = aTurn;
    vAlpha = aAlpha;
}
`;

/**
 * a thing's normals from its map, turned as the thing is (aTurn: the turn's cosine and sine, read off the picture's
 * first axis, and -1 for a mirrored picture: mirrored along its second axis then, the turn being the first's)
 */
export const NORMALS_FRAGMENT = /* glsl */`precision highp float;
in vec2 vUV;
in vec3 vTurn;
in float vAlpha;
out vec4 finalColor;

uniform sampler2D uAtlas;

void main(void) {
    vec4 s = texture(uAtlas, vUV);
    if (s.a <= 0.002) {
        finalColor = vec4(0.0);
        return;
    }
    vec3 n = (s.rgb / s.a) * 2.0 - 1.0;
    n.y *= vTurn.z;
    vec2 turned = vec2(n.x * vTurn.x - n.y * vTurn.y, n.x * vTurn.y + n.y * vTurn.x);
    float a = s.a * vAlpha;
    finalColor = vec4((vec3(turned, n.z) * 0.5 + 0.5) * a, a);
}
`;
